import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  PageHeader,
  Spinner,
  EmptyState,
  Badge,
  Modal,
  Field,
  Select,
  Tabs,
  ErrorBanner,
} from '../components/ui';

interface Booking {
  id: string;
  startsAt: string;
  endsAt: string;
  status: string;
  contactId: string;
  serviceId: string;
  notes?: string;
}

interface Service {
  id: string;
  name: string;
  durationMinutes: number;
}

interface Contact {
  id: string;
  firstName: string;
  lastName?: string;
}

type View = 'list' | 'week' | 'month';

export function CalendarPage() {
  const [view, setView] = useState<View>('list');
  const [showCreate, setShowCreate] = useState(false);
  const [anchorDate, setAnchorDate] = useState(new Date());
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['bookings', 'all'],
    queryFn: () => api.get<{ data: Booking[] }>('/bookings', { perPage: '100' }).catch(() => ({ data: [] })),
  });

  const bookings = q.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Calendar"
        subtitle={`${bookings.length} bookings`}
        action={<Button onClick={() => setShowCreate(true)}>+ New booking</Button>}
      />
      <Tabs<View>
        active={view}
        onChange={setView}
        tabs={[
          { value: 'list', label: 'List' },
          { value: 'week', label: 'Week' },
          { value: 'month', label: 'Month' },
        ]}
      />
      {q.isLoading ? (
        <Spinner />
      ) : view === 'list' ? (
        <ListView bookings={bookings} />
      ) : view === 'week' ? (
        <WeekView bookings={bookings} anchorDate={anchorDate} onChangeDate={setAnchorDate} />
      ) : (
        <MonthView bookings={bookings} anchorDate={anchorDate} onChangeDate={setAnchorDate} />
      )}

      {showCreate && (
        <CreateBookingModal
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['bookings'] });
            setShowCreate(false);
          }}
        />
      )}
    </div>
  );
}

function ListView({ bookings }: { bookings: Booking[] }) {
  const now = new Date();
  const upcoming = bookings
    .filter((b) => new Date(b.startsAt) >= now && b.status !== 'cancelled')
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());

  const byDay: Record<string, Booking[]> = {};
  for (const b of upcoming) {
    const day = new Date(b.startsAt).toDateString();
    (byDay[day] ??= []).push(b);
  }

  if (upcoming.length === 0) {
    return (
      <Card>
        <EmptyState title="Nothing on the calendar" description="Create a booking or share your public booking link." />
      </Card>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {Object.entries(byDay).map(([day, items]) => (
        <Card key={day}>
          <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 600 }}>{day}</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {items.map((b) => (
              <li key={b.id} style={{ padding: 'var(--space-3) 0', borderBottom: '1px solid var(--color-border-primary)', display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                    {new Date(b.startsAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    {' → '}
                    {new Date(b.endsAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </div>
                  {b.notes && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 2 }}>{b.notes}</div>}
                </div>
                <Badge color={b.status === 'confirmed' ? 'green' : b.status === 'completed' ? 'gray' : 'blue'}>{b.status}</Badge>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}

function WeekView({ bookings, anchorDate, onChangeDate }: { bookings: Booking[]; anchorDate: Date; onChangeDate: (d: Date) => void }) {
  const days = useMemo(() => {
    const start = new Date(anchorDate);
    start.setDate(start.getDate() - start.getDay()); // Sunday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [anchorDate]);

  const byDay: Record<string, Booking[]> = {};
  for (const b of bookings) {
    if (b.status === 'cancelled') continue;
    const day = new Date(b.startsAt).toDateString();
    (byDay[day] ??= []).push(b);
  }

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
        <Button
          variant="ghost"
          onClick={() => {
            const d = new Date(anchorDate);
            d.setDate(d.getDate() - 7);
            onChangeDate(d);
          }}
        >
          ← Prev
        </Button>
        <strong>
          {days[0].toLocaleDateString()} - {days[6].toLocaleDateString()}
        </strong>
        <Button
          variant="ghost"
          onClick={() => {
            const d = new Date(anchorDate);
            d.setDate(d.getDate() + 7);
            onChangeDate(d);
          }}
        >
          Next →
        </Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 'var(--space-1)' }}>
        {days.map((d) => (
          <div key={d.toISOString()} style={{ background: 'var(--color-surface-secondary)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', minHeight: 120 }}>
            <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>
              {d.toLocaleDateString(undefined, { weekday: 'short' })} {d.getDate()}
            </div>
            {(byDay[d.toDateString()] ?? []).map((b) => (
              <div key={b.id} style={{ background: 'var(--color-brand-primary)', color: 'white', padding: '2px 6px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-xs)', marginBottom: 2 }}>
                {new Date(b.startsAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </div>
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
}

function MonthView({ bookings, anchorDate, onChangeDate }: { bookings: Booking[]; anchorDate: Date; onChangeDate: (d: Date) => void }) {
  const cells = useMemo(() => {
    const first = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
    const start = new Date(first);
    start.setDate(start.getDate() - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [anchorDate]);

  const byDay: Record<string, Booking[]> = {};
  for (const b of bookings) {
    if (b.status === 'cancelled') continue;
    const day = new Date(b.startsAt).toDateString();
    (byDay[day] ??= []).push(b);
  }

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
        <Button
          variant="ghost"
          onClick={() => {
            const d = new Date(anchorDate);
            d.setMonth(d.getMonth() - 1);
            onChangeDate(d);
          }}
        >
          ← Prev
        </Button>
        <strong>{anchorDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</strong>
        <Button
          variant="ghost"
          onClick={() => {
            const d = new Date(anchorDate);
            d.setMonth(d.getMonth() + 1);
            onChangeDate(d);
          }}
        >
          Next →
        </Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, background: 'var(--color-border-primary)' }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} style={{ background: 'var(--color-surface-secondary)', padding: 'var(--space-1)', textAlign: 'center', fontSize: 'var(--text-xs)', fontWeight: 600 }}>
            {d}
          </div>
        ))}
        {cells.map((d) => {
          const inMonth = d.getMonth() === anchorDate.getMonth();
          const items = byDay[d.toDateString()] ?? [];
          return (
            <div
              key={d.toISOString()}
              style={{
                background: 'var(--color-surface-primary)',
                padding: 'var(--space-1)',
                minHeight: 80,
                opacity: inMonth ? 1 : 0.4,
              }}
            >
              <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, marginBottom: 2 }}>{d.getDate()}</div>
              {items.slice(0, 3).map((b) => (
                <div key={b.id} style={{ background: 'var(--color-brand-primary)', color: 'white', padding: '1px 4px', borderRadius: 'var(--radius-sm)', fontSize: 10, marginBottom: 2 }}>
                  {new Date(b.startsAt).toLocaleTimeString([], { hour: 'numeric' })}
                </div>
              ))}
              {items.length > 3 && <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>+{items.length - 3}</div>}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function CreateBookingModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [contactId, setContactId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState('09:00');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const contactsQ = useQuery({
    queryKey: ['contacts', 'pick'],
    queryFn: () => api.get<{ data: Contact[] }>('/contacts', { limit: '200' }),
  });

  const servicesQ = useQuery({
    queryKey: ['services', 'pick'],
    queryFn: () => api.get<{ data: Service[] }>('/services').catch(() => ({ data: [] })),
  });

  const mut = useMutation({
    mutationFn: () => {
      const startsAt = new Date(`${date}T${time}:00`).toISOString();
      return api.post('/bookings', { contactId, serviceId, startsAt, notes: notes || undefined });
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  const contacts = contactsQ.data?.data ?? [];
  const services = servicesQ.data?.data ?? [];

  return (
    <Modal onClose={onClose} title="New booking">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!contactId || !serviceId) return setError('Pick contact and service');
          mut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Contact *">
          <select value={contactId} onChange={(e) => setContactId(e.target.value)} style={selectStyle}>
            <option value="">Select…</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.firstName} {c.lastName ?? ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Service *">
          <select value={serviceId} onChange={(e) => setServiceId(e.target.value)} style={selectStyle}>
            <option value="">Select…</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.durationMinutes} min)
              </option>
            ))}
          </select>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
          <Field label="Date">
            <Input value={date} onChange={setDate} type="date" />
          </Field>
          <Field label="Time">
            <Input value={time} onChange={setTime} type="time" />
          </Field>
        </div>
        <Field label="Notes">
          <Input value={notes} onChange={setNotes} />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Saving…' : 'Create booking'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

const selectStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  border: '1px solid var(--color-border-primary)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  background: 'var(--color-surface-primary)',
};
